import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import Chart from "react-apexcharts";
import { getUserLogs, getUserMetrics } from "../../services/api/logsService.ts";

const UserDashboard = () => {
    const { userId } = useParams();
    const [userInfo, setUserInfo] = useState({ name: '', surname: '' });
    const navigate = useNavigate();
    const isMountedRef = useRef(true);

    const [logs, setLogs] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [chapterStats, setChapterStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        isMountedRef.current = true;
        setLoading(true);
        setError(null);

        Promise.all([
            getUserLogs(userId).then(data => {
                if (isMountedRef.current) setLogs(data || []);
            }),
            getUserMetrics(userId).then(data => {
                if (isMountedRef.current) setMetrics(data);
            }),
            fetch(`/api/logs/user/${userId}/by-chapter`)
                .then(res => res.json())
                .then(data => {
                    if (!isMountedRef.current) return;
                    const validStats = data.filter(c => c && c.chapter).map(c => ({
                        ...c,
                        chapter: String(c.chapter || 'Unknown'),
                        avgScore: Number(c.avgScore) || 0,
                        logsCount: Number(c.logsCount) || 0,
                        lastScore: Number(c.lastScore || c.avgScore) || 0
                    }));
                    setChapterStats(validStats);
                })
        ])
            .catch(err => {
                console.error(err);
                if (isMountedRef.current) setError("Erreur lors du chargement des données");
            })
            .finally(() => {
                if (isMountedRef.current) setLoading(false);
            });

        // Récupérer la liste des utilisateurs et trouver le bon
        fetch('/api/user/list')
            .then(res => res.json())
            .then(users => {
                if (!Array.isArray(users)) return;
                const user = users.find(u => String(u.user_ID) === String(userId));
                if (user && isMountedRef.current) {
                    setUserInfo({ name: user.user_NAME, surname: user.user_SURNAME });
                }
            });

        // Cleanup function
        return () => {
            isMountedRef.current = false;
        };
    }, [userId]);

    const handleChapterClick = (chapterIndex) => {
        if (chapterIndex !== undefined && chapterStats[chapterIndex]) {
            const chapterName = chapterStats[chapterIndex].chapter;
            // Navigate immediately without waiting
            navigate(`/user/${userId}/chapter/${encodeURIComponent(chapterName)}`, { replace: true });
        }
    };

    if (loading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="alert alert-danger">{error}</div>
                </Container>
            </div>
        );
    }

    // Vérification si l'utilisateur n'a pas de logs
    const hasNoLogs = !logs || logs.length === 0 || !metrics || metrics.totalLogs === 0;

    if (hasNoLogs) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Col lg={12}>
                        <div className="card">
                            <div className="card-body">
                                <h1 className="text-primary text-center">Tableau de bord</h1>
                                <h2 className="text-tertiary text-center">
                                    {userInfo.name} {userInfo.surname}
                                </h2>
                                <p className="text-muted mb-2 text-center">Utilisateur #{userId}</p>
                            </div>
                        </div>
                    </Col>

                    <div className="text-center py-5">
                        <i className="mdi mdi-chart-box-outline" style={{ fontSize: '64px', color: '#ccc' }}></i>
                        <h4 className="text-muted mt-3">Pas de scores pour cet utilisateur</h4>
                        <p className="text-muted">Aucune activité n'a encore été enregistrée.</p>
                    </div>
                </Container>
            </div>
        );
    }

    // Calculate global average safely
    const globalAvgScore = chapterStats.length > 0
        ? chapterStats.map(c => Number(c.avgScore || 0))
        : [];

    const chapterSeries = [
        {
            name: "Dernier score (%)",
            type: "column",
            data: chapterStats.map(c => Math.round(Number(c.lastScore)) || 0),
        },
        {
            name: "Moyenne du chapitre (%)",
            type: "line",
            data: chapterStats.map(c => Math.round(Number(c.avgScore) * 100) / 100),
        },
        {
            name: "Nombre d'essais",
            type: "line",
            data: chapterStats.map(c => Math.round(Number(c.logsCount)) || 0),
        }
    ];

    const chapterOptions = {
        chart: {
            stacked: false,
            toolbar: { show: false },
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    // Stop event propagation
                    if (event) {
                        event.stopPropagation();
                    }

                    const chapterIndex = config.dataPointIndex;

                    // Use setTimeout to ensure navigation happens after chart event completes
                    setTimeout(() => {
                        handleChapterClick(chapterIndex);
                    }, 0);
                }
            }
        },
        stroke: {
            width: [1, 1, 1],
            dashArray: [0, 0, 5],
            curve: "smooth"
        },
        colors: ['#31c0fc', '#0d4498', '#74788d'],
        fill: {
            opacity: [0.6, 0.85, 0.85],
            gradient: {
                inverseColors: false,
                shade: "light",
                type: "vertical",
                opacityFrom: 0.85,
                opacityTo: 0.55,
                stops: [0, 100, 100, 100],
            },
        },
        xaxis: {
            categories: chapterStats.map(c => String(c.chapter || 'Unknown')),
            title: { text: "Référentiels" }
        },
        dataLabels: {
            enabled: true,
            formatter: function (val, opts) {
                if (opts.seriesIndex === 0) {
                    return `Dernier score: ${Math.round(val)}`;
                }
                //return Math.round(val);
            },

            style: { fontSize: '12px', colors: ['#304758'] },
            offsetY: -10
        },
        yaxis: [
            {
                title: { text: "Dernier score (%)" },
                min: 0,
                /*max: 100*/
                labels: {
                    formatter: function (val) {
                        return Math.round(val);
                    }
                },
            },
            {
                show: false,
                opposite: true,
                labels: {
                    formatter: function (val) {
                        return Number(val).toFixed(0);
                    }
                }
            },
            {
                title: { text: "Nombre d'essais" },
                show: true,
                opposite: true,
                labels: {
                    formatter: function (val) {
                        return Math.round(val);
                    }
                }
            },
        ],
        tooltip: {
            shared: true,
            intersect: false
        },
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Col lg={12}>
                        <div classname="card">
                            <div className="card-body">
                                <h1 className="text-primary text-center">Tableau de bord</h1>
                                <h2 className="text-tertiary text-center">
                                    {userInfo.name} {userInfo.surname}
                                </h2>
                                <p className="text-muted mb-2 text-center">Utilisateur # {userId}</p>
                            </div>
                        </div>
                    </Col>
                    <Row>
                        <Col md={4}>
                            <Card><CardBody><i className="mdi mdi-school fs-3 text-primary"></i> Quizz réalisés : {metrics.totalLogs}</CardBody></Card>
                        </Col>
                        <Col md={4}>
                            <Card><CardBody><i className="mdi mdi-chart-areaspline fs-3 text-primary"></i> Score moyen : {metrics.avgScore}%</CardBody></Card>
                        </Col>
                        <Col md={4}>
                            <Card><CardBody><i className="mdi mdi-timer fs-3 text-primary"></i> Temps moyen : {metrics.avgTime} min</CardBody></Card>
                        </Col>
                    </Row>

                    <Row className="mt-4">
                        <Col lg={12}>
                            <Card>
                                <CardBody style={{ minHeight: 350 }}>
                                    <h5>Performances par référentiel</h5>
                                    {chapterStats.length > 0 ? (
                                        <Chart
                                            options={chapterOptions}
                                            series={chapterSeries}
                                            type="line"
                                            height={350}
                                        />
                                    ) : (
                                        <div className="text-center py-5">
                                            <i className="mdi mdi-information-outline" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                            <p className="text-muted mt-3">Aucune donnée par chapitre disponible.</p>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <div className="mb-3 mt-4 d-flex justify-content-center">
                        <button
                            title="Retour à la liste des utilisateurs"
                            className="btn btn-outline-primary" onClick={() => window.history.back()}>
                            <i className="mdi mdi-backspace-outline fs-4 py-0 align-middle"></i> Retour au dashboard administrateur
                        </button>
                    </div>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default UserDashboard;


//Creators : Florian Cohen-Joly  @3DDGAMES -- Under license
